/**
 * A {@link Transport} to the core of the **desktop app** (`host-desktop`, a Tauri window).
 * The core runs natively in the app's process; the frames are the same JSON texts a WebSocket
 * would carry, so the rest of the GUI is unchanged.
 *
 * - requests go through the `core_send` command, one after the other, so they reach the core
 *   in the order they were sent;
 * - answers and events come back as `core-frame` events.
 *
 * Tauri's API is read from `window.__TAURI__` (`withGlobalTauri` in `tauri.conf.json`), so
 * the browser builds need no extra package.
 */
import type { Transport } from "./client";

/** The part of Tauri's global API this transport uses. */
export interface TauriApi {
  core: {
    /** Calls a Rust command. */
    invoke(command: string, args: Record<string, unknown>): Promise<unknown>;
  };
  event: {
    /** Listens to an event; resolves with a function that stops listening. */
    listen(event: string, handler: (event: { payload: unknown }) => void): Promise<() => void>;
  };
  /** The system's file dialogs (Tauri's dialog plugin), when the app includes it. */
  dialog?: {
    /** Shows an "open" dialog; resolves with the chosen path(s), or `null` if cancelled. */
    open(options: { directory: boolean; multiple: boolean; title?: string }): Promise<string | string[] | null>;
  };
}

/**
 * A function that asks the user for a folder with the system's dialog, when the desktop app
 * offers one; `undefined` otherwise (in a browser, the path is typed instead).
 */
export function tauriFolderPicker(api: TauriApi | undefined): (() => Promise<string | null>) | undefined {
  const dialog = api?.dialog;
  if (!dialog) return undefined;
  return async () => {
    const chosen = await dialog.open({ directory: true, multiple: false, title: "Project folder to analyze" });
    return Array.isArray(chosen) ? (chosen[0] ?? null) : chosen;
  };
}

/** Name of the Rust command that takes one request frame (see `host-desktop`). */
export const SEND_COMMAND = "core_send";

/** Name of the event that carries one frame from the core. */
export const FRAME_EVENT = "core-frame";

/** Tauri's API when the page runs inside the desktop app, otherwise `undefined`. */
export function tauriApi(): TauriApi | undefined {
  return (globalThis as { __TAURI__?: TauriApi }).__TAURI__;
}

/** A transport over Tauri's channel between the window and the native core. */
export function tauriTransport(api: TauriApi): Transport {
  /** Stops listening for frames (set once listening). */
  let unlisten: (() => void) | null = null;
  /** Each send waits for the previous one, so the core gets them in order. */
  let queue: Promise<unknown> = Promise.resolve();
  let closedForGood = false;
  let open: () => void = () => {};
  let close: () => void = () => {};
  let message: (text: string) => void = () => {};

  return {
    connect() {
      if (closedForGood) return;
      if (unlisten) {
        queueMicrotask(open);
        return;
      }
      api.event
        .listen(FRAME_EVENT, (event) => typeof event.payload === "string" && message(event.payload))
        .then((stop) => {
          unlisten = stop;
          open();
        })
        .catch((error: unknown) => {
          console.error("cannot listen to the core:", error);
          close();
        });
    },
    send(text) {
      if (!unlisten || closedForGood) return false;
      queue = queue
        .then(() => api.core.invoke(SEND_COMMAND, { text }))
        .catch((error: unknown) => console.error("the core did not take a request:", error));
      return true;
    },
    close() {
      closedForGood = true;
      unlisten?.();
      unlisten = null;
    },
    onOpen(handler) {
      open = handler;
    },
    onClose(handler) {
      close = handler;
    },
    onMessage(handler) {
      message = handler;
    },
  };
}
